import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Layers, Upload, Plus, FileText, History, Eye, 
  ChevronRight, ChevronDown, Filter, Search, MoreVertical,
  Pencil, Trash2, Download, Share2, Clock, User, CheckCircle,
  AlertTriangle, X, ZoomIn, Edit3, GitCompare, SlidersHorizontal,
  Link2, Ruler, StickyNote, Send, ListChecks
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PlanRoomHomeCard from '@/components/PlanRoomHomeCard';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import '@/styles/site-office.css';
import DrawingMarkupEditor from '@/components/DrawingMarkupEditor';
import HelpIcon from '@/components/HelpIcon';
import { drawingImageProps, pickPreviewMode } from '@/utils/drawingImage';
import DrawingCard from '@/components/DrawingCard';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
// v7 (2026-07-12): drawingThumb.js deleted — DrawingCard renders directly.
// Debug toggle inlined below via localStorage key 'keymade.drawingThumbnails.debug'.
import PlanRoomPanel from '@/components/PlanRoomPanel';
import { ClientVisibleSwitch, portalDrawingsNote } from '@/components/ClientVisibleSwitch';
import { useAuthedFileUrl, downloadAuthedFile, printAuthedFile, viewAuthedFile } from '@/utils/authedFile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DISCIPLINES = [
  { value: 'architectural', label: 'Architectural (A)', color: 'bg-blue-500' },
  { value: 'structural', label: 'Structural (S)', color: 'bg-gray-500' },
  { value: 'electrical', label: 'Electrical (E)', color: 'bg-yellow-500' },
  { value: 'plumbing', label: 'Plumbing (P)', color: 'bg-cyan-500' },
  { value: 'mechanical', label: 'Mechanical (M)', color: 'bg-orange-500' },
  { value: 'civil', label: 'Civil (C)', color: 'bg-green-500' },
  { value: 'interior', label: 'Interior (I)', color: 'bg-purple-500' },
  { value: 'landscape', label: 'Landscape (L)', color: 'bg-emerald-500' },
];

const DRAWING_TYPES = [
  { value: 'floor_plan', label: 'Floor Plan' },
  { value: 'elevation_exterior', label: 'Elevation - Exterior' },
  { value: 'elevation_interior', label: 'Elevation - Interior' },
  { value: 'electrical_plan', label: 'Electrical Plan' },
  { value: 'cabinet_layout', label: 'Cabinet Layout' },
  { value: 'roof_plan', label: 'Roof Plan' },
  { value: 'foundation_plan', label: 'Foundation Plan' },
  { value: 'site_plan', label: 'Site Plan' },
  { value: 'section', label: 'Section' },
  { value: 'detail', label: 'Detail' },
  { value: 'schedule', label: 'Schedule' },
  { value: 'mood_board', label: 'Mood Board' },
  { value: 'other', label: 'Other' },
];

// Fix Round v4 — DrawingThumbnail component handles the ladder + client
// PDF render + diagnostics. StackedDrawingCard is now a thin wrapper.
function StackedDrawingCard({ 
  drawing, 
  thumbnailUrl,  // legacy — no longer used, kept for backwards call signature
  disciplineConfig, 
  versionCount, 
  latestVersion, 
  darkMode, 
  onView, 
  onUploadVersion,
  onDelete,
  onToggleClient,
  BACKEND_URL
}) {
  // v7 (2026-07-12): pass is_super_admin to <DrawingCard> so the on-card
  // url/status/hadToken diagnostic renders when a preview fails. Non-SAs
  // see only the plain orange "Preview failed" badge.
  const isSuperAdmin = useIsSuperAdmin();
  return (
    <div className="relative">
      <div
        className={`group relative cursor-pointer rounded-xl border-2 overflow-hidden transition-all duration-300 ${
          darkMode ? 'border-[#3A3A3A] bg-[#1A1A1A] hover:border-[#BC5D41]' : 'border-[#D5D2CD] bg-white hover:border-[#BC5D41]'
        }`}
        style={{
          boxShadow: `0 4px 12px rgba(0,0,0,${darkMode ? 0.4 : 0.12})`,
        }}
        data-testid={`drawing-card-${drawing.id}`}
      >
        {/* Thumbnail */}
        <div 
          className={`aspect-[4/3] relative ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#FAF9F7]'}`}
          onClick={() => onView(drawing)}
        >
          <DrawingCard
isSuperAdmin={isSuperAdmin}
            drawing={drawing}
            className="w-full h-full object-cover"
            fallbackIconClassName={`w-12 h-12 ${darkMode ? 'text-[#4A4A4A]' : 'text-gray-300'}`}
            testId={`drawing-card-thumb-${drawing.id}`}
          />
          
          {/* Version badge */}
          <div className="absolute top-2 right-2">
            <Badge className={`text-[10px] font-semibold ${darkMode ? 'bg-[#2A2A2A] text-[#E5E2DD] border border-[#3A3A3A]' : 'bg-black/70 text-white'}`}>
              v{drawing.current_version_number || 1}
            </Badge>
          </div>
          
          {/* Broken-state badge (Build 13.6.1) — kept.
              v5: pointer-events-none so the DrawingThumbnail debug
              overlay's Health-check button remains clickable when
              the drawing genuinely has no file. */}
          {(!drawing.versions || drawing.versions.length === 0) && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-red-500/10 backdrop-blur-[1px] pointer-events-none"
              data-testid={`drawing-broken-badge-${drawing.id}`}
            >
              <Badge className="bg-red-600 text-white text-[10px] font-semibold shadow-lg">
                No file — upload v1
              </Badge>
            </div>
          )}

          {/* Discipline indicator */}
          <div className={`absolute top-2 left-2 w-3.5 h-3.5 rounded-full border-2 ${darkMode ? 'border-[#2A2A2A]' : 'border-white'} ${disciplineConfig.color}`} />
          
          {/* Version count indicator — badge only, no stack visual */}
          {versionCount > 1 && (
            <div className="absolute bottom-2 right-2">
              <Badge className="bg-amber-500 text-black text-[9px] font-semibold shadow-md" data-testid={`drawing-card-version-count-${drawing.id}`}>
                <Layers className="w-2.5 h-2.5 mr-0.5" />
                {versionCount} versions
              </Badge>
            </div>
          )}
        </div>
      
        {/* Info */}
        <div className={`p-3 ${darkMode ? 'bg-[#1A1A1A]' : 'bg-white'}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-mono text-xs font-semibold ${darkMode ? 'text-[#E07A5F]' : 'text-[#BC5D41]'}`}>{drawing.drawing_number}</span>
          </div>
          <h3 className={`text-sm font-semibold truncate ${darkMode ? 'text-[#F5F5F5]' : 'text-[#1A1918]'}`}>
            {drawing.title}
          </h3>
          
          {/* Notes - visible description */}
          {drawing.notes && (
            <p className={`text-[10px] mt-1 line-clamp-2 ${darkMode ? 'text-[#9A9A9A]' : 'text-[#6B6965]'}`}>
              <StickyNote className="w-2.5 h-2.5 inline mr-1" />
              {drawing.notes}
            </p>
          )}
          
          <div className={`flex items-center gap-2 mt-2 text-[10px] ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
            <Clock className="w-3 h-3" />
            <span>{latestVersion?.uploaded_at ? new Date(latestVersion.uploaded_at).toLocaleDateString() : 'No versions'}</span>
            {drawing.revision_count > 0 && (
              <>
                <span>•</span>
                <History className="w-3 h-3 text-yellow-500" />
                <span className="text-yellow-500">{drawing.revision_count} rev</span>
              </>
            )}
          </div>

          <div className="kdc-client-row">
            <ClientVisibleSwitch drawing={drawing} onToggle={onToggleClient} />
          </div>
          
          {/* Quick actions on hover */}
          <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[10px]"
              onClick={(e) => { e.stopPropagation(); onUploadVersion(drawing); }}
            >
              <Upload className="w-3 h-3 mr-1" />
              Version
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[10px] text-red-500 hover:text-red-600"
              onClick={(e) => { e.stopPropagation(); onDelete(drawing); }}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DrawingControlPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  // Dark mode state - sync with system preference
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('keyMadeDarkMode') === 'true';
    }
    return false;
  });
  
  const [drawings, setDrawings] = useState([]);
  const [byDiscipline, setByDiscipline] = useState({});
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [portalNote, setPortalNote] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDiscipline, setSelectedDiscipline] = useState('all');
  const [selectedDrawingType, setSelectedDrawingType] = useState('all');
  const [expandedDisciplines, setExpandedDisciplines] = useState({});
  const [viewMode, setViewMode] = useState('thumbnail'); // 'thumbnail' | 'list'
  
  // Listen for dark mode changes from other components
  useEffect(() => {
    const handleStorage = () => {
      setDarkMode(localStorage.getItem('keyMadeDarkMode') === 'true');
    };
    window.addEventListener('storage', handleStorage);
    // Also check on mount in case changed while navigating
    const interval = setInterval(() => {
      const current = localStorage.getItem('keyMadeDarkMode') === 'true';
      if (current !== darkMode) setDarkMode(current);
    }, 500);
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, [darkMode]);
  
  // Create Drawing Dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newDrawing, setNewDrawing] = useState({
    title: '',
    discipline: 'architectural',
    drawing_type: 'floor_plan',
    scale: '1/4 inch = 1 foot',
    sheet_size: '24x36',
    source_type: 'file', // 'file', 'link', 'takeoff'
    source_url: '',
    source_takeoff_id: '',
    notes: '' // Visible description on thumbnail
  });
  const [uploadingDrawing, setUploadingDrawing] = useState(false);
  const [drawingFile, setDrawingFile] = useState(null);
  
  // Floor plans from takeoff (for source selection)
  const [takeoffPlans, setTakeoffPlans] = useState([]);
  
  // Upload Version Dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const [versionFile, setVersionFile] = useState(null);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const [newVersion, setNewVersion] = useState({
    file_url: '',
    file_name: '',
    revision_notes: '',
    uploaded_by: 'Designer',
    source_type: 'file', // 'file', 'link', 'takeoff'
    takeoff_id: ''
  });
  
  // View Drawing Dialog
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingDrawing, setViewingDrawing] = useState(null);
  // Build 13.5 — the drawing file lives at an auth-gated backend route;
  // `<iframe src>` and `window.open()` can't attach the JWT header. The
  // hook fetches with auth, returns a same-origin `blob:` URL that
  // iframe/img/anchor can consume. Cleaned up on unmount/URL change.
  const {
    blobUrl: viewingDrawingBlobUrl,
    loading: viewingDrawingLoading,
    status: viewingDrawingStatus,
  } = useAuthedFileUrl(showViewDialog ? viewingDrawing?.current_file_url : null);
  
  // Markup Editor
  const [showMarkupEditor, setShowMarkupEditor] = useState(false);
  const [markupVersion, setMarkupVersion] = useState(null);
  
  // Version Comparison
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [compareVersions, setCompareVersions] = useState({ v1: null, v2: null });
  const [overlayOpacity, setOverlayOpacity] = useState(50);
  const [compareMode, setCompareMode] = useState('side-by-side'); // 'side-by-side' | 'overlay'

  useEffect(() => {
    fetchData();
  }, [projectId]);

  useEffect(() => {
    axios.get(`${API}/projects/${projectId}/portal-desk`)
      .then((res) => setPortalNote(portalDrawingsNote(res.data)))
      .catch(() => setPortalNote(null));
  }, [projectId]);

  const toggleClientVisible = async (drawing) => {
    const next = !drawing.client_visible;
    try {
      await axios.patch(`${API}/projects/${projectId}/drawings/${drawing.id}/client-visibility`,
        { client_visible: next });
      setDrawings((list) => list.map((d) => (d.id === drawing.id ? { ...d, client_visible: next } : d)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change what the client sees');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [drawingsRes, projectRes, floorPlansRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/drawings`),
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/floor-plans`).catch(() => ({ data: { floor_plans: [] } }))
      ]);
      
      setDrawings(drawingsRes.data.drawings || []);
      setByDiscipline(drawingsRes.data.by_discipline || {});
      setProject(projectRes.data);
      
      // Extract floor plans - handle both array and object response
      const plans = floorPlansRes.data?.floor_plans || floorPlansRes.data || [];
      setTakeoffPlans(Array.isArray(plans) ? plans : []);
      
      // Expand all disciplines by default
      const expanded = {};
      Object.keys(drawingsRes.data.by_discipline || {}).forEach(d => {
        expanded[d] = true;
      });
      setExpandedDisciplines(expanded);
    } catch (error) {
      console.error('Error fetching drawings:', error);
      toast.error('Failed to load drawings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDrawing = async () => {
    if (!newDrawing.title) {
      toast.error('Please enter a drawing title');
      return;
    }
    
    // Validate source based on type
    if (newDrawing.source_type === 'takeoff' && !newDrawing.source_takeoff_id) {
      toast.error('Please select a plan from Takeoff');
      return;
    }
    
    if (newDrawing.source_type === 'file' && !drawingFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    try {
      setUploadingDrawing(true);
      
      if (newDrawing.source_type === 'file' && drawingFile) {
        // Use file upload endpoint
        const formData = new FormData();
        formData.append('file', drawingFile);
        formData.append('title', newDrawing.title);
        formData.append('discipline', newDrawing.discipline);
        formData.append('drawing_type', newDrawing.drawing_type);
        formData.append('notes', newDrawing.notes || '');
        formData.append('scale', newDrawing.scale || '');
        formData.append('sheet_size', newDrawing.sheet_size);
        
        const response = await axios.post(`${API}/projects/${projectId}/drawings/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success(`Drawing ${response.data.drawing.drawing_number} created`);
      } else {
        // Use JSON endpoint for link or takeoff sources
        const response = await axios.post(`${API}/projects/${projectId}/drawings`, newDrawing);
        toast.success(`Drawing ${response.data.drawing.drawing_number} created`);
      }
      
      setShowCreateDialog(false);
      setDrawingFile(null);
      setNewDrawing({
        title: '',
        discipline: 'architectural',
        drawing_type: 'floor_plan',
        scale: '1/4 inch = 1 foot',
        sheet_size: '24x36',
        source_type: 'file',
        source_url: '',
        source_takeoff_id: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating drawing:', error);
      toast.error(error.response?.data?.detail || 'Failed to create drawing');
    } finally {
      setUploadingDrawing(false);
    }
  };

  const handleUploadVersion = async () => {
    // Validate based on source type
    const sourceType = newVersion.source_type || 'file';
    if (sourceType === 'takeoff' && !newVersion.takeoff_id) {
      toast.error('Please select a plan from Takeoff');
      return;
    }
    
    if (sourceType === 'file' && !versionFile && !newVersion.file_url) {
      toast.error('Please select a file to upload');
      return;
    }
    
    try {
      setUploadingVersion(true);
      
      if (versionFile) {
        // Use file upload endpoint
        const formData = new FormData();
        formData.append('file', versionFile);
        formData.append('revision_notes', newVersion.revision_notes || '');
        
        const response = await axios.post(
          `${API}/drawings/${selectedDrawing.id}/versions/upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        toast.success(`Version ${response.data.version.version_number} uploaded. ${response.data.preserved_markup_count} markups preserved.`);
      } else {
        // Use JSON endpoint
        const response = await axios.post(
          `${API}/drawings/${selectedDrawing.id}/versions`,
          newVersion
        );
        toast.success(`Version ${response.data.version.version_number} uploaded. ${response.data.preserved_markup_count} markups preserved.`);
      }
      
      setShowUploadDialog(false);
      setSelectedDrawing(null);
      setVersionFile(null);
      setNewVersion({
        file_url: '',
        file_name: '',
        revision_notes: '',
        uploaded_by: 'Designer',
        source_type: 'file',
        takeoff_id: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error uploading version:', error);
      toast.error('Failed to upload version');
    }
  };

  const openVersionUpload = (drawing) => {
    setSelectedDrawing(drawing);
    setShowUploadDialog(true);
  };

  const viewDrawing = async (drawing) => {
    try {
      const response = await axios.get(`${API}/drawings/${drawing.id}`);
      setViewingDrawing(response.data);
      setShowViewDialog(true);
    } catch (error) {
      console.error('Error fetching drawing details:', error);
      toast.error('Failed to load drawing details');
    }
  };

  const openMarkupEditor = (drawing, version) => {
    setViewingDrawing(drawing);
    setMarkupVersion(version);
    setShowMarkupEditor(true);
  };

  // DP274-P3a-1 — the editor hands over only the marks it touched; the server
  // merges them and hands back the whole sheet, numbers and all.
  const saveMarkups = async (markups) => {
    if (!viewingDrawing || !markupVersion) return null;
    try {
      const res = await axios.post(
        `${API}/drawings/${viewingDrawing.id}/versions/${markupVersion.id}/markups`,
        { markups });
      fetchData();
      return res.data?.markups || null;
    } catch (error) {
      console.error('Error saving markups:', error);
      toast.error('Failed to save marks');
      return null;
    }
  };

  // Delete drawing
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [drawingToDelete, setDrawingToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  const confirmDeleteDrawing = (drawing) => {
    setDrawingToDelete(drawing);
    setShowDeleteConfirm(true);
  };
  
  const deleteDrawing = async () => {
    if (!drawingToDelete) return;
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    
    try {
      await axios.delete(`${API}/drawings/${drawingToDelete.id}`);
      toast.success('Drawing deleted');
      setShowDeleteConfirm(false);
      setDrawingToDelete(null);
      setDeleteConfirmText('');
      fetchData();
    } catch (error) {
      console.error('Error deleting drawing:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete drawing');
    }
  };

  // Open version comparison dialog
  const openVersionCompare = (drawing, selectedVersion = null) => {
    const versions = drawing.versions || [];
    if (versions.length < 2) {
      toast.error('Need at least 2 versions to compare');
      return;
    }
    
    // Default: compare latest with previous
    const sortedVersions = [...versions].sort((a, b) => b.version_number - a.version_number);
    setCompareVersions({
      v1: selectedVersion || sortedVersions[1], // older
      v2: sortedVersions[0] // newer (current)
    });
    setViewingDrawing(drawing);
    setShowCompareDialog(true);
  };

  // View a specific version
  const viewVersion = (drawing, version) => {
    setViewingDrawing(drawing);
    setMarkupVersion(version);
    // If there's a file URL, open it in a new tab
    if (version.file_url) {
      window.open(version.file_url, '_blank');
    }
  };

  const toggleDiscipline = (discipline) => {
    setExpandedDisciplines(prev => ({
      ...prev,
      [discipline]: !prev[discipline]
    }));
  };

  const getDisciplineConfig = (discipline) => {
    return DISCIPLINES.find(d => d.value === discipline) || { label: discipline, color: 'bg-gray-500' };
  };

  const filteredDrawings = drawings.filter(d => {
    const matchesSearch = !searchQuery || 
      (d.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (d.drawing_number || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDiscipline = selectedDiscipline === 'all' || d.discipline === selectedDiscipline;
    const matchesType = selectedDrawingType === 'all' || d.drawing_type === selectedDrawingType;
    return matchesSearch && matchesDiscipline && matchesType;
  });

  // Group filtered drawings by discipline
  const filteredByDiscipline = filteredDrawings.reduce((acc, d) => {
    const disc = d.discipline || 'other';
    if (!acc[disc]) acc[disc] = [];
    acc[disc].push(d);
    return acc;
  }, {});

  // Group filtered drawings by type (for thumbnail view)
  const filteredByType = filteredDrawings.reduce((acc, d) => {
    const type = d.drawing_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(d);
    return acc;
  }, {});

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#FAF9F7]'}`} data-testid="drawing-control-page">
      {/* Header */}
      <header className={`sticky top-0 z-40 border-b backdrop-blur ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]/95' : 'border-[#E5E2DD] bg-white/95'}`}>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(projectId ? `/projects/${projectId}/hub` : '/')}
                className={darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {project?.name || 'Back'}
              </Button>
              <div className={`h-6 w-px ${darkMode ? 'bg-[#1A1A1A]' : 'bg-[#E5E2DD]'}`} />
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-[#FDF8F6]'}`}>
                  <Layers className="w-5 h-5 text-[#BC5D41]" />
                </div>
                <div className="flex items-center gap-2">
                  <HelpIcon elementId="drawing-control" position="inline" />
                  <div>
                    <h1 className={`font-heading font-light text-xl ${darkMode ? 'text-white' : 'text-[#1A1918]'}`} data-testid="plan-room-title">Plan Room</h1>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`}>{drawings.length} drawings • Issued register & slip-sheeting</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Fix Round v4 — Super-admin thumbnail debug toggle.
                  SA detection reads the JWT's middle segment (login
                  writes flat keys, no 'user' blob). Falls closed on
                  any parse error. */}
              {(() => {
                let isSA = false;
                try {
                  const tok = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
                  const mid = tok.split('.')[1];
                  if (mid) {
                    const pad = '='.repeat((4 - (mid.length % 4)) % 4);
                    const b64 = (mid + pad).replace(/-/g, '+').replace(/_/g, '/');
                    const payload = JSON.parse(atob(b64));
                    isSA = !!payload.is_super_admin;
                  }
                } catch { /* fall closed */ }
                if (!isSA) return null;
                const on = (() => { try { return localStorage.getItem('keymade.drawingThumbnails.debug') === '1'; } catch { return false; } })();
                return (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      try {
                        if (on) localStorage.removeItem('keymade.drawingThumbnails.debug');
                        else localStorage.setItem('keymade.drawingThumbnails.debug', '1');
                        window.dispatchEvent(new CustomEvent('keymade:thumbdebug', { detail: { on: !on } }));
                      } catch { /* localStorage may be blocked */ }
                    }}
                    className={`text-xs ${on ? 'bg-lime-500/20 border-lime-500 text-lime-700' : ''}`}
                    data-testid="thumbnail-debug-toggle"
                    title="Overlay each card's thumbnail ladder state (super-admin)"
                  >
                    Thumbnail debug: {on ? 'ON' : 'OFF'}
                  </Button>
                );
              })()}
              {/* Quick link to Takeoff */}
              <Button
                variant="outline"
                onClick={() => navigate(`/projects/${projectId}/floor-plans`)}
                className={`${darkMode ? 'border-[#3A3A3A] text-[#E5E2DD] hover:bg-[#1A1A1A]' : 'border-[#E5E2DD] text-[#6B6965] hover:bg-gray-50'}`}
              >
                <Layers className="w-4 h-4 mr-2" />
                Open Takeoff
              </Button>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Drawing
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* DP85 — the Plan Room summary card moved here off the Project Desk. The desk
          is five bands; the latest issued set belongs at the Plan Room. */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-6" data-testid="plan-room-moved-card">
        <PlanRoomHomeCard projectId={projectId} darkMode={darkMode} />
      </div>

      {/* Filters */}
      <div className={`border-b ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search drawings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-10 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-white' : 'bg-white border-[#E5E2DD] text-[#1A1918]'}`}
              />
            </div>
            <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
              <SelectTrigger className={`w-44 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-white' : 'bg-white border-[#E5E2DD] text-[#1A1918]'}`}>
                <SelectValue placeholder="All Disciplines" />
              </SelectTrigger>
              <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                <SelectItem value="all" className={darkMode ? 'text-white' : ''}>All Disciplines</SelectItem>
                {DISCIPLINES.map(d => (
                  <SelectItem key={d.value} value={d.value} className={darkMode ? 'text-white' : ''}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedDrawingType} onValueChange={setSelectedDrawingType}>
              <SelectTrigger className={`w-44 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-white' : 'bg-white border-[#E5E2DD] text-[#1A1918]'}`}>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                <SelectItem value="all" className={darkMode ? 'text-white' : ''}>All Types</SelectItem>
                {DRAWING_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value} className={darkMode ? 'text-white' : ''}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1 border rounded-lg p-0.5" style={{ borderColor: darkMode ? '#2A2A2A' : '#E5E2DD' }}>
              <Button
                variant={viewMode === 'thumbnail' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('thumbnail')}
                className={`h-8 px-3 ${viewMode === 'thumbnail' ? 'bg-[#70170F] text-white' : ''}`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={`h-8 px-3 ${viewMode === 'list' ? 'bg-[#70170F] text-white' : ''}`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <line x1="4" y1="6" x2="20" y2="6" strokeWidth="2" />
                  <line x1="4" y1="12" x2="20" y2="12" strokeWidth="2" />
                  <line x1="4" y1="18" x2="20" y2="18" strokeWidth="2" />
                </svg>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Drawing List */}
      <main className="max-w-[1600px] mx-auto px-6 md:px-12 py-8">
        {/* Plan Room register — Sets + Transmittals band. */}
        <PlanRoomPanel
          projectId={projectId}
          drawings={drawings}
          darkMode={darkMode}
          onSetIssued={fetchData}
        />
        {loading ? (
          <div className="text-center py-24">
            <p className={darkMode ? 'text-gray-500' : 'text-[#6B6965]'}>Loading drawings...</p>
          </div>
        ) : filteredDrawings.length === 0 ? (
          <div className="text-center py-24">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6 ${darkMode ? 'bg-[#1A1A1A]' : 'bg-[#FAF9F7]'}`}>
              <Layers className={`w-10 h-10 ${darkMode ? 'text-gray-600' : 'text-[#6B6965]'}`} />
            </div>
            <h2 className={`font-heading text-2xl mb-2 ${darkMode ? 'text-white' : 'text-[#1A1918]'}`}>No drawings yet</h2>
            <p className={`mb-6 ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`}>
              Create your first drawing to start version control
            </p>
            <Button onClick={() => setShowCreateDialog(true)} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Create Drawing
            </Button>
          </div>
        ) : (
          <>
            {/* Thumbnail View */}
            {viewMode === 'thumbnail' ? (
              <div className="space-y-8">
                {Object.entries(filteredByType).map(([type, typeDrawings]) => {
                  const typeConfig = DRAWING_TYPES.find(t => t.value === type) || { label: type, value: type };
                  
                  return (
                    <div key={type}>
                      <div className="flex items-center gap-3 mb-4">
                        <h2 className={`font-heading text-lg ${darkMode ? 'text-white' : 'text-[#1A1918]'}`}>
                          {typeConfig.label}
                        </h2>
                        <Badge variant="secondary" className={darkMode ? 'bg-[#1A1A1A] text-gray-400' : 'bg-[#FAF9F7] text-[#6B6965]'}>
                          {typeDrawings.length}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                        {typeDrawings.map((drawing) => {
                          const latestVersion = drawing.versions?.[drawing.versions.length - 1];
                          // Only use actual thumbnail URLs (not PDFs) - check for image extensions
                          const isImageUrl = (url) => url && /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(url);
                          const thumbnailUrl = drawing.thumbnail_url || latestVersion?.thumbnail_url || 
                            (isImageUrl(latestVersion?.file_url) ? latestVersion?.file_url : null) ||
                            (isImageUrl(drawing.current_file_url) ? drawing.current_file_url : null);
                          const disciplineConfig = getDisciplineConfig(drawing.discipline);
                          const versionCount = drawing.versions?.length || 1;
                          
                          return (
                            <StackedDrawingCard
                              key={drawing.id}
                              drawing={drawing}
                              thumbnailUrl={thumbnailUrl}
                              disciplineConfig={disciplineConfig}
                              versionCount={versionCount}
                              latestVersion={latestVersion}
                              darkMode={darkMode}
                              onView={viewDrawing}
                              onUploadVersion={openVersionUpload}
                              onDelete={confirmDeleteDrawing}
                              onToggleClient={toggleClientVisible}
                              BACKEND_URL={BACKEND_URL}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* List View (Original) */
              <div className="space-y-6">
                {Object.entries(filteredByDiscipline).map(([discipline, disciplineDrawings]) => {
                  const config = getDisciplineConfig(discipline);
                  const isExpanded = expandedDisciplines[discipline] !== false;
                  
                  return (
                    <div key={discipline} className={`rounded-xl border overflow-hidden ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
                      {/* Discipline Header */}
                      <button
                        onClick={() => toggleDiscipline(discipline)}
                        className={`w-full flex items-center justify-between p-4 transition-colors ${darkMode ? 'hover:bg-[#1A1A1A]/50' : 'hover:bg-[#FAF9F7]'}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${config.color}`} />
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-[#1A1918]'}`}>{config.label}</span>
                          <Badge variant="secondary" className={darkMode ? 'bg-[#1A1A1A] text-gray-400' : 'bg-[#FAF9F7] text-[#6B6965]'}>
                            {disciplineDrawings.length}
                          </Badge>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className={`w-5 h-5 ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`} />
                        ) : (
                          <ChevronRight className={`w-5 h-5 ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`} />
                        )}
                      </button>
                      
                      {/* Drawings List */}
                      {isExpanded && (
                        <div className={`border-t ${darkMode ? 'border-[#1A1A1A]' : 'border-[#E5E2DD]'}`}>
                          {disciplineDrawings.map((drawing, idx) => (
                            <div
                              key={drawing.id}
                              className={`flex items-center justify-between p-4 transition-colors ${
                                darkMode ? 'hover:bg-[#1A1A1A]/30' : 'hover:bg-[#FAF9F7]'
                              } ${
                                idx !== disciplineDrawings.length - 1 ? (darkMode ? 'border-b border-[#1A1A1A]' : 'border-b border-[#E5E2DD]') : ''
                              }`}
                            >
                              <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${darkMode ? 'bg-[#1A1A1A]' : 'bg-[#FAF9F7]'}`}>
                                  <FileText className={`w-6 h-6 ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-sm text-[#BC5D41]">{drawing.drawing_number}</span>
                                    <span className={darkMode ? 'text-white' : 'text-[#1A1918]'}>{drawing.title}</span>
                                  </div>
                                  <div className={`flex items-center gap-3 mt-1 text-xs ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`}>
                                    <span className="capitalize">{drawing.drawing_type?.replace('_', ' ')}</span>
                                    {drawing.scale && <span>• {drawing.scale}</span>}
                                    <span>• v{drawing.current_version_number || 0}</span>
                                    {drawing.revision_count > 0 && (
                                      <span className="text-yellow-500">• {drawing.revision_count} revisions</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <ClientVisibleSwitch drawing={drawing} onToggle={toggleClientVisible} />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => viewDrawing(drawing)}
                                  className={darkMode ? 'text-gray-400 hover:text-white' : 'text-[#6B6965] hover:text-[#1A1918]'}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openVersionUpload(drawing)}
                                  className={darkMode ? 'border-[#2A2A2A] text-gray-300 hover:bg-[#1A1A1A]' : 'border-[#E5E2DD] text-[#6B6965] hover:bg-[#FAF9F7]'}
                                >
                                  <Upload className="w-4 h-4 mr-1" />
                                  New Version
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className={darkMode ? 'text-gray-400' : 'text-[#6B6965]'}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                                <DropdownMenuItem 
                                  className={`${darkMode ? 'text-white' : ''} text-[#BC5D41]`}
                                  onClick={() => navigate(`/projects/${projectId}/floor-plans?drawing=${drawing.id}`)}
                                >
                                  <Ruler className="w-4 h-4 mr-2" />
                                  Open in Takeoff
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className={darkMode ? 'text-white' : ''}
                                  onClick={() => {
                                    setViewingDrawing(drawing);
                                    setShowViewDialog(true);
                                  }}
                                >
                                  <History className="w-4 h-4 mr-2" />
                                  Version History
                                </DropdownMenuItem>
                                {(drawing.versions?.length || 0) >= 2 && (
                                  <DropdownMenuItem 
                                    className={darkMode ? 'text-white' : ''}
                                    onClick={() => openVersionCompare(drawing)}
                                  >
                                    <GitCompare className="w-4 h-4 mr-2" />
                                    Compare Versions
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem className={darkMode ? 'text-white' : ''}>
                                  <Download className="w-4 h-4 mr-2" />
                                  Download Current
                                </DropdownMenuItem>
                                <DropdownMenuItem className={darkMode ? 'text-white' : ''}>
                                  <Share2 className="w-4 h-4 mr-2" />
                                  Share
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-red-600 focus:text-red-600"
                                  onClick={() => confirmDeleteDrawing(drawing)}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Drawing
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
            )}
          </>
        )}
        {portalNote && (
          <p className="kdc-portal-note" data-testid="drawing-client-portal-note">{portalNote}</p>
        )}
      </main>

      {/* Create Drawing Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-xl bg-[#0D0D0D] border-[#1A1A1A] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#BC5D41]" />
              New Drawing
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Create a new drawing for version control
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Title */}
            <div>
              <Label className="text-gray-300">Title *</Label>
              <Input
                placeholder="e.g., First Floor Plan"
                value={newDrawing.title}
                onChange={(e) => setNewDrawing(prev => ({ ...prev, title: e.target.value }))}
                className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />
            </div>
            
            {/* Notes - Visible on thumbnail */}
            <div>
              <Label className="text-gray-300">Notes (visible on thumbnail)</Label>
              <Textarea
                placeholder="Brief description visible without opening..."
                value={newDrawing.notes}
                onChange={(e) => setNewDrawing(prev => ({ ...prev, notes: e.target.value }))}
                className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white h-16"
              />
            </div>
            
            {/* Source Type */}
            <div>
              <Label className="text-gray-300">Source</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setNewDrawing(prev => ({ ...prev, source_type: 'file' }))}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    newDrawing.source_type === 'file'
                      ? 'border-[#BC5D41] bg-[#BC5D41]/10 text-white'
                      : 'border-[#2A2A2A] text-gray-400 hover:border-gray-500'
                  }`}
                >
                  <Upload className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs">File Upload</span>
                </button>
                <button
                  type="button"
                  onClick={() => setNewDrawing(prev => ({ ...prev, source_type: 'link' }))}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    newDrawing.source_type === 'link'
                      ? 'border-[#BC5D41] bg-[#BC5D41]/10 text-white'
                      : 'border-[#2A2A2A] text-gray-400 hover:border-gray-500'
                  }`}
                >
                  <Link2 className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs">Link/URL</span>
                </button>
                <button
                  type="button"
                  onClick={() => setNewDrawing(prev => ({ ...prev, source_type: 'takeoff' }))}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    newDrawing.source_type === 'takeoff'
                      ? 'border-[#BC5D41] bg-[#BC5D41]/10 text-white'
                      : 'border-[#2A2A2A] text-gray-400 hover:border-gray-500'
                  }`}
                >
                  <Ruler className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs">From Takeoff</span>
                </button>
              </div>
            </div>
            
            {/* Source Input based on type */}
            {newDrawing.source_type === 'file' && (
              <div>
                <Label className="text-gray-300">Upload File</Label>
                <div 
                  className={`mt-2 border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                    drawingFile 
                      ? 'border-[#BC5D41] bg-[#BC5D41]/10' 
                      : 'border-[#2A2A2A] hover:border-gray-500'
                  }`}
                  onClick={() => document.getElementById('drawing-file-input').click()}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      setDrawingFile(file);
                      if (!newDrawing.title) {
                        setNewDrawing(prev => ({ ...prev, title: file.name.replace(/\.[^.]+$/, '') }));
                      }
                    }
                  }}
                >
                  <input
                    id="drawing-file-input"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.tiff,.tif"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setDrawingFile(file);
                        if (!newDrawing.title) {
                          setNewDrawing(prev => ({ ...prev, title: file.name.replace(/\.[^.]+$/, '') }));
                        }
                      }
                    }}
                  />
                  {drawingFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="w-8 h-8 text-[#BC5D41]" />
                      <div className="text-left">
                        <p className="text-sm text-white font-medium">{drawingFile.name}</p>
                        <p className="text-xs text-gray-400">{(drawingFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      <button 
                        type="button" 
                        onClick={(e) => { e.stopPropagation(); setDrawingFile(null); }}
                        className="text-gray-400 hover:text-red-400"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">Click to browse or drag & drop</p>
                      <p className="text-xs text-gray-500 mt-1">PDF, PNG, JPG, TIFF supported</p>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {newDrawing.source_type === 'link' && (
              <div>
                <Label className="text-gray-300">External Link</Label>
                <Input
                  placeholder="https://drive.google.com/... or Dropbox link"
                  value={newDrawing.source_url}
                  onChange={(e) => setNewDrawing(prev => ({ ...prev, source_url: e.target.value }))}
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                />
              </div>
            )}
            
            {newDrawing.source_type === 'takeoff' && (
              <div>
                <Label className="text-gray-300">Select from Takeoff</Label>
                {takeoffPlans.length > 0 ? (
                  <div className="mt-2 grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {takeoffPlans.map(plan => {
                      const planUrl = plan.file_url || plan.image_url || plan.url;
                      // T2b-3a — a plan's own name first; its file name only when it has none.
                      const planName = plan.name || plan.original_name || plan.filename || 'Floor Plan';
                      return (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() => setNewDrawing(prev => ({ 
                            ...prev, 
                            source_takeoff_id: plan.id,
                            source_url: planUrl,
                            title: prev.title || planName.replace(/\.[^.]+$/, '') // Remove file extension
                          }))}
                          className={`p-2 rounded-lg border text-left transition-all ${
                            newDrawing.source_takeoff_id === plan.id
                              ? 'border-[#BC5D41] bg-[#BC5D41]/10'
                              : 'border-[#2A2A2A] hover:border-gray-500'
                          }`}
                        >
                          <div className="w-full h-16 bg-[#1A1A1A] rounded mb-1 overflow-hidden flex items-center justify-center">
                            {plan.thumbnail_url ? (
                              <img src={plan.thumbnail_url.startsWith('http') ? plan.thumbnail_url : `${BACKEND_URL}${plan.thumbnail_url}`} alt={planName} className="w-full h-full object-cover" />
                            ) : (
                              <FileText className="w-8 h-8 text-gray-600" />
                            )}
                          </div>
                          <span className="text-xs text-gray-300 truncate block">{planName}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">No floor plans in takeoff yet. Upload plans in the Takeoff Tool first.</p>
                )}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Discipline</Label>
                <Select
                  value={newDrawing.discipline}
                  onValueChange={(v) => setNewDrawing(prev => ({ ...prev, discipline: v }))}
                >
                  <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DISCIPLINES.map(d => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Type</Label>
                <Select
                  value={newDrawing.drawing_type}
                  onValueChange={(v) => setNewDrawing(prev => ({ ...prev, drawing_type: v }))}
                >
                  <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DRAWING_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Scale</Label>
                <Input
                  placeholder="1/4 inch = 1 foot"
                  value={newDrawing.scale}
                  onChange={(e) => setNewDrawing(prev => ({ ...prev, scale: e.target.value }))}
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Sheet Size</Label>
                <Select
                  value={newDrawing.sheet_size}
                  onValueChange={(v) => setNewDrawing(prev => ({ ...prev, sheet_size: v }))}
                >
                  <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="8.5x11">8.5x11 (Letter)</SelectItem>
                    <SelectItem value="11x17">11x17 (Tabloid)</SelectItem>
                    <SelectItem value="24x36">24x36 (Arch D)</SelectItem>
                    <SelectItem value="30x42">30x42 (Arch E)</SelectItem>
                    <SelectItem value="36x48">36x48 (Arch E1)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); setDrawingFile(null); }} className="border-[#2A2A2A]">
              Cancel
            </Button>
            <Button onClick={handleCreateDrawing} disabled={uploadingDrawing} className="bg-[#70170F] hover:bg-[#70170F]/90">
              {uploadingDrawing ? 'Uploading...' : 'Create Drawing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Version Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-xl bg-[#0D0D0D] border-[#1A1A1A] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#BC5D41]" />
              Upload New Version
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              {selectedDrawing && (
                <span>
                  Uploading to <span className="text-[#BC5D41]">{selectedDrawing.drawing_number}</span> - {selectedDrawing.title}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A]">
              <div className="flex items-center gap-2 text-sm text-yellow-500">
                <AlertTriangle className="w-4 h-4" />
                Slip-Sheeting Active
              </div>
              <p className="text-xs text-gray-500 mt-1">
                All markups from the current version will be preserved on the new version.
              </p>
            </div>
            
            {/* Source Type Selection */}
            <div>
              <Label className="text-gray-300">Source</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setNewVersion(prev => ({ ...prev, source_type: 'file' }))}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    (newVersion.source_type || 'file') === 'file'
                      ? 'border-[#BC5D41] bg-[#BC5D41]/10 text-white'
                      : 'border-[#2A2A2A] text-gray-400 hover:border-gray-500'
                  }`}
                >
                  <Upload className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs">File Upload</span>
                </button>
                <button
                  type="button"
                  onClick={() => setNewVersion(prev => ({ ...prev, source_type: 'link' }))}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    newVersion.source_type === 'link'
                      ? 'border-[#BC5D41] bg-[#BC5D41]/10 text-white'
                      : 'border-[#2A2A2A] text-gray-400 hover:border-gray-500'
                  }`}
                >
                  <Link2 className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs">Link/URL</span>
                </button>
                <button
                  type="button"
                  onClick={() => setNewVersion(prev => ({ ...prev, source_type: 'takeoff' }))}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    newVersion.source_type === 'takeoff'
                      ? 'border-[#BC5D41] bg-[#BC5D41]/10 text-white'
                      : 'border-[#2A2A2A] text-gray-400 hover:border-gray-500'
                  }`}
                >
                  <Layers className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs">From Takeoff</span>
                </button>
              </div>
            </div>
            
            {/* Source Input based on type */}
            {(newVersion.source_type || 'file') === 'file' && (
              <div>
                <Label className="text-gray-300">Upload File</Label>
                <div 
                  className={`mt-2 border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                    versionFile 
                      ? 'border-[#BC5D41] bg-[#BC5D41]/10' 
                      : 'border-[#2A2A2A] hover:border-gray-500'
                  }`}
                  onClick={() => document.getElementById('version-file-input').click()}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = e.dataTransfer.files[0];
                    if (file) setVersionFile(file);
                  }}
                >
                  <input
                    id="version-file-input"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.tiff,.tif"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) setVersionFile(file);
                    }}
                  />
                  {versionFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="w-8 h-8 text-[#BC5D41]" />
                      <div className="text-left">
                        <p className="text-sm text-white font-medium">{versionFile.name}</p>
                        <p className="text-xs text-gray-400">{(versionFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      <button 
                        type="button" 
                        onClick={(e) => { e.stopPropagation(); setVersionFile(null); }}
                        className="text-gray-400 hover:text-red-400"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">Click to browse or drag & drop</p>
                      <p className="text-xs text-gray-500 mt-1">PDF, PNG, JPG, TIFF supported</p>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {newVersion.source_type === 'link' && (
              <div>
                <Label className="text-gray-300">External Link</Label>
                <Input
                  placeholder="https://drive.google.com/... or Dropbox link"
                  value={newVersion.file_url}
                  onChange={(e) => setNewVersion(prev => ({ ...prev, file_url: e.target.value }))}
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                />
              </div>
            )}
            
            {newVersion.source_type === 'takeoff' && (
              <div>
                <Label className="text-gray-300">Select from Takeoff Layers</Label>
                {takeoffPlans.length > 0 ? (
                  <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                    {takeoffPlans.map(plan => {
                      const isLinked = selectedDrawing?.source_takeoff_id === plan.id;
                      return (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() => setNewVersion(prev => ({ 
                            ...prev, 
                            file_url: plan.image_url || plan.url,
                            takeoff_id: plan.id
                          }))}
                          className={`w-full p-2 rounded-lg border text-left transition-all flex items-center gap-3 ${
                            newVersion.takeoff_id === plan.id
                              ? 'border-[#BC5D41] bg-[#BC5D41]/10'
                              : 'border-[#2A2A2A] hover:border-gray-500'
                          }`}
                        >
                          <div className="w-12 h-12 bg-[#1A1A1A] rounded overflow-hidden flex-shrink-0">
                            {(plan.image_url || plan.url) && (
                              <img src={plan.image_url || plan.url} alt={plan.name} className="w-full h-full object-cover" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-300 truncate block">{plan.name}</span>
                            {isLinked && (
                              <span className="text-[10px] text-[#BC5D41]">✓ Currently linked</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">No floor plans in takeoff. Open a drawing in Takeoff first to create annotation layers.</p>
                )}
              </div>
            )}
            
            <div>
              <Label className="text-gray-300">File Name</Label>
              <Input
                placeholder="A-101_Rev2.pdf"
                value={newVersion.file_name}
                onChange={(e) => setNewVersion(prev => ({ ...prev, file_name: e.target.value }))}
                className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Revision Notes</Label>
              <Textarea
                placeholder="Updated kitchen layout per client feedback..."
                value={newVersion.revision_notes}
                onChange={(e) => setNewVersion(prev => ({ ...prev, revision_notes: e.target.value }))}
                className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)} className="border-[#2A2A2A]">
              Cancel
            </Button>
            <Button onClick={handleUploadVersion} className="bg-[#70170F] hover:bg-[#70170F]/90">
              Upload Version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Drawing Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className={`sm:max-w-4xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A] text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#BC5D41]" />
                <span className={darkMode ? 'text-white' : 'text-gray-900'}>{viewingDrawing?.drawing_number} - {viewingDrawing?.title}</span>
              </div>
              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {viewingDrawing?.current_file_url && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className={`h-8 text-xs ${darkMode ? 'border-[#2A2A2A] text-white hover:bg-[#2A2A2A]' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                      onClick={async () => {
                        const ok = await viewAuthedFile(viewingDrawing.current_file_url);
                        if (!ok) toast.error('Could not open drawing — check your session');
                      }}
                      data-testid="drawing-view-btn"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className={`h-8 text-xs ${darkMode ? 'border-[#2A2A2A] text-white hover:bg-[#2A2A2A]' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                      onClick={async () => {
                        const filename = `${viewingDrawing.drawing_number || 'drawing'}_${viewingDrawing.title || 'file'}.pdf`;
                        const ok = await downloadAuthedFile(viewingDrawing.current_file_url, filename);
                        if (!ok) toast.error('Download failed — check your session');
                      }}
                      data-testid="drawing-download-btn"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className={`h-8 text-xs ${darkMode ? 'border-[#2A2A2A] text-white hover:bg-[#2A2A2A]' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                      onClick={async () => {
                        const ok = await printAuthedFile(viewingDrawing.current_file_url);
                        if (!ok) toast.error('Print failed — pop-up blocker or session expired');
                      }}
                      data-testid="drawing-print-btn"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Print
                    </Button>
                    {/* Stage D ride-along #5 — Send to Plan Room (lands in tenant Unfiled) */}
                    <Button
                      size="sm"
                      variant="outline"
                      className={`h-8 text-xs ${darkMode ? 'border-[#2A2A2A] text-white hover:bg-[#2A2A2A]' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                      onClick={async () => {
                        try {
                          const body = {
                            attachment_url: viewingDrawing.current_file_url,
                            title: `${viewingDrawing.drawing_number || ''} ${viewingDrawing.title || ''}`.trim() || viewingDrawing.title || 'Drawing',
                            filename: (viewingDrawing.current_file_url || '').split('/').pop(),
                            sender: 'Drawing Control',
                            subject: `Sent from Drawing Control — ${viewingDrawing.drawing_number || viewingDrawing.title || ''}`,
                          };
                          await axios.post(`${API}/plan-room/unfiled/from-email`, body);
                          toast.success('Sent to Plan Room — landed in tenant Unfiled');
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Failed to send to Plan Room');
                        }
                      }}
                      data-testid="drawing-send-to-plan-room-btn"
                    >
                      <Send className="w-3 h-3 mr-1" />
                      Send to Plan Room
                    </Button>
                  </>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          {viewingDrawing && (
            <div className="space-y-6 py-4">
              {/* Drawing Preview */}
              {viewingDrawing.current_file_url && (
                <div className={`rounded-lg border overflow-hidden ${darkMode ? 'border-[#2A2A2A] bg-[#1A1A1A]' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="aspect-[4/3] relative">
                    {viewingDrawingLoading && !viewingDrawingBlobUrl ? (
                      <div className="w-full h-full flex items-center justify-center text-sm text-gray-500">
                        Loading drawing…
                      </div>
                    ) : !viewingDrawingBlobUrl ? (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-sm text-red-700 bg-red-50/50">
                        <span data-testid="viewer-preview-error">
                          {(() => {
                            const s = viewingDrawingStatus;
                            if (s === 404) return 'File not found (404). The stored URL does not resolve.';
                            if (s === 401) return 'Authentication failed (401). Try signing in again.';
                            if (s === 403) return 'Access denied (403).';
                            if (s) return `Server error (${s}).`;
                            return 'Network error — could not reach the server.';
                          })()}
                        </span>
                        <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
                          Reload
                        </Button>
                      </div>
                    ) : (() => {
                      // Fix Round 2026-07-10 — robust PDF detection (handles
                      // Dropbox `?raw=1`, `#page=2`, etc.). Prior code used
                      // `endsWith('.pdf')` which broke on any URL suffix,
                      // rendering PDF bytes into <img> as a broken image
                      // (Shelton's viewer bug).
                      const previewMode = pickPreviewMode(viewingDrawing.current_file_url);
                      if (previewMode === 'image') {
                        return (
                          <img
                            src={viewingDrawingBlobUrl}
                            alt={viewingDrawing.title}
                            className="w-full h-full object-contain"
                            data-testid="drawing-preview-img"
                          />
                        );
                      }
                      // Default to iframe (PDF or unknown). Browsers can
                      // display images inside iframes; the reverse is not
                      // true, so iframe is the safe fallback.
                      return (
                        <iframe
                          src={`${viewingDrawingBlobUrl}#view=FitH`}
                          className="w-full h-full"
                          title={viewingDrawing.title}
                          data-testid="drawing-preview-iframe"
                        />
                      );
                    })()}
                  </div>
                </div>
              )}
              
              {/* Notes */}
              {viewingDrawing.notes && (
                <div className={`p-3 rounded-lg border ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-gray-50 border-gray-200'}`}>
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Notes:</span>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{viewingDrawing.notes}</p>
                </div>
              )}
              
              {/* Drawing Info */}
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Discipline:</span>
                  <span className={`ml-2 capitalize ${darkMode ? 'text-white' : 'text-gray-900'}`}>{viewingDrawing.discipline}</span>
                </div>
                <div>
                  <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Type:</span>
                  <span className={`ml-2 capitalize ${darkMode ? 'text-white' : 'text-gray-900'}`}>{viewingDrawing.drawing_type?.replace('_', ' ')}</span>
                </div>
                <div>
                  <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Scale:</span>
                  <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{viewingDrawing.scale || 'Not set'}</span>
                </div>
                <div>
                  <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Sheet Size:</span>
                  <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{viewingDrawing.sheet_size}</span>
                </div>
              </div>
              
              {/* Version History */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-sm font-medium flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <History className="w-4 h-4" />
                    Version History ({viewingDrawing.versions?.length || 0})
                  </h3>
                  <div className="flex items-center gap-2">
                    {(viewingDrawing.versions?.length || 0) >= 2 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10"
                        onClick={() => {
                          setShowViewDialog(false);
                          openVersionCompare(viewingDrawing);
                        }}
                      >
                        <GitCompare className="w-3 h-3 mr-1" />
                        Compare Versions
                      </Button>
                    )}
                  </div>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(viewingDrawing.versions || []).slice().reverse().map((version, idx) => (
                    <div
                      key={version.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        version.status === 'current' 
                          ? `bg-[#70170F]/20 border-[#70170F] hover:bg-[#70170F]/30` 
                          : `${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] hover:bg-[#252525]' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`
                      }`}
                      onClick={() => {
                        if (version.file_url) {
                          const url = version.file_url.startsWith('http') ? version.file_url : `${BACKEND_URL}${version.file_url}`;
                          window.open(url, '_blank');
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-[#BC5D41]">v{version.version_number}</span>
                          {version.status === 'current' && (
                            <Badge className="bg-[#70170F] text-white text-xs">Current</Badge>
                          )}
                          {version.file_url && (
                            <Eye className={`w-3 h-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                          )}
                        </div>
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          {new Date(version.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {version.revision_notes && (
                        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{version.revision_notes}</p>
                      )}
                      <div className={`flex items-center gap-4 mt-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {version.uploaded_by || 'Unknown'}
                        </span>
                        {version.preserved_markups?.length > 0 && (
                          <span className="flex items-center gap-1 text-yellow-500">
                            <Pencil className="w-3 h-3" />
                            {version.preserved_markups.length} preserved markups
                          </span>
                        )}
                        {version.markups?.length > 0 && (
                          <span className="flex items-center gap-1 text-green-500">
                            <Pencil className="w-3 h-3" />
                            {version.markups.length} new markups
                          </span>
                        )}
                        {/* DP274-P3a-1 — a sheet with questions on it says so. */}
                        {(() => {
                          const open = [...(version.markups || []), ...(version.preserved_markups || [])]
                            .filter(m => (m.status || 'open') === 'open' && m.number).length;
                          return open > 0 ? (
                            <span className="kso-tag wait" data-testid={`version-open-notes-${version.id}`}>
                              {open} open notes
                            </span>
                          ) : null;
                        })()}
                      </div>
                      {/* Version Actions */}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {version.file_url && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-6 px-2 text-[10px] ${darkMode ? 'text-gray-400 hover:text-white hover:bg-[#2A2A2A]' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const url = version.file_url.startsWith('http') ? version.file_url : `${BACKEND_URL}${version.file_url}`;
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `${viewingDrawing.drawing_number}_v${version.version_number}.pdf`;
                                link.click();
                              }}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Download
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-6 px-2 text-[10px] ${darkMode ? 'text-gray-400 hover:text-white hover:bg-[#2A2A2A]' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const url = version.file_url.startsWith('http') ? version.file_url : `${BACKEND_URL}${version.file_url}`;
                                const printWindow = window.open(url, '_blank');
                                if (printWindow) {
                                  printWindow.onload = () => printWindow.print();
                                }
                              }}
                            >
                              <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                              </svg>
                              Print
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-6 px-2 text-[10px] ${darkMode ? 'text-gray-400 hover:text-white hover:bg-[#2A2A2A]' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const url = version.file_url.startsWith('http') ? version.file_url : `${BACKEND_URL}${version.file_url}`;
                                navigator.clipboard.writeText(url);
                                toast.success('Link copied to clipboard');
                              }}
                            >
                              <Share2 className="w-3 h-3 mr-1" />
                              Copy Link
                            </Button>
                          </>
                        )}
                        {version.status === 'current' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-[10px] border-[#BC5D41] text-[#BC5D41] hover:bg-[#BC5D41]/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              openMarkupEditor(viewingDrawing, version);
                            }}
                          >
                            <Edit3 className="w-3 h-3 mr-1" />
                            Add Markups
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!viewingDrawing.versions || viewingDrawing.versions.length === 0) && (
                    <p className={`text-sm text-center py-4 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>No versions uploaded yet</p>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex items-center justify-between">
            <Button 
              variant="outline" 
              onClick={() => setShowViewDialog(false)} 
              className={darkMode ? 'border-[#2A2A2A] text-white' : 'border-gray-300'}
            >
              Close
            </Button>
            {viewingDrawing && (
              <div className="flex items-center gap-2">
                {/* Open in Takeoff - for editing with layers */}
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowViewDialog(false);
                    navigate(`/projects/${projectId}/floor-plans?drawing=${viewingDrawing.id}`);
                  }}
                  className="border-[#BC5D41] text-[#BC5D41] hover:bg-[#BC5D41]/10"
                >
                  <Layers className="w-4 h-4 mr-2" />
                  Edit in Takeoff
                </Button>
                {/* Upload new version - upload a new file */}
                <Button
                  onClick={() => {
                    setShowViewDialog(false);
                    openVersionUpload(viewingDrawing);
                  }}
                  className="bg-[#70170F] hover:bg-[#70170F]/90"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload New File
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Markup Editor */}
      {showMarkupEditor && markupVersion && (
        <DrawingMarkupEditor
          imageUrl={markupVersion.file_url}
          existingMarkups={[...(markupVersion.preserved_markups || []), ...(markupVersion.markups || [])]}
          onSave={saveMarkups}
          onSaveMerged={saveMarkups}
          authorKind="firm"
          apiBase={`${API}/drawings/${viewingDrawing?.id}/versions/${markupVersion.id}`}
          onClose={() => setShowMarkupEditor(false)}
          drawingTitle={viewingDrawing?.title || 'Drawing'}
          drawingNumber={viewingDrawing?.drawing_number || ''}
          projectName={project?.name || ''}
          versionNumber={markupVersion.version_number || 1}
        />
      )}

      {/* Version Comparison Dialog */}
      <Dialog open={showCompareDialog} onOpenChange={setShowCompareDialog}>
        <DialogContent className={`max-w-6xl max-h-[90vh] ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
              <GitCompare className="w-5 h-5 text-[#3B82F6]" />
              Compare Versions - {viewingDrawing?.title}
            </DialogTitle>
            <DialogDescription className={darkMode ? 'text-gray-400' : ''}>
              Compare changes between drawing versions. {compareMode === 'overlay' ? 'Use the slider to adjust transparency.' : 'Side by side comparison.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Controls */}
            <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-4">
                {/* Version Selectors */}
                <div className="flex items-center gap-2">
                  <Label className={`text-xs ${darkMode ? 'text-gray-400' : ''}`}>Older:</Label>
                  <Select 
                    value={compareVersions.v1?.id || ''} 
                    onValueChange={(val) => {
                      const v = viewingDrawing?.versions?.find(v => v.id === val);
                      setCompareVersions(prev => ({ ...prev, v1: v }));
                    }}
                  >
                    <SelectTrigger className={`w-32 h-8 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-white' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                      {(viewingDrawing?.versions || []).map(v => (
                        <SelectItem key={v.id} value={v.id} className={darkMode ? 'text-white' : ''}>
                          v{v.version_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className={`text-xs ${darkMode ? 'text-gray-400' : ''}`}>Newer:</Label>
                  <Select 
                    value={compareVersions.v2?.id || ''} 
                    onValueChange={(val) => {
                      const v = viewingDrawing?.versions?.find(v => v.id === val);
                      setCompareVersions(prev => ({ ...prev, v2: v }));
                    }}
                  >
                    <SelectTrigger className={`w-32 h-8 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-white' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                      {(viewingDrawing?.versions || []).map(v => (
                        <SelectItem key={v.id} value={v.id} className={darkMode ? 'text-white' : ''}>
                          v{v.version_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Mode Toggle */}
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant={compareMode === 'side-by-side' ? 'default' : 'outline'}
                  className={compareMode === 'side-by-side' ? 'bg-[#3B82F6]' : `${darkMode ? 'border-[#2A2A2A] text-gray-400' : ''}`}
                  onClick={() => setCompareMode('side-by-side')}
                >
                  Side by Side
                </Button>
                <Button
                  size="sm"
                  variant={compareMode === 'overlay' ? 'default' : 'outline'}
                  className={compareMode === 'overlay' ? 'bg-[#3B82F6]' : `${darkMode ? 'border-[#2A2A2A] text-gray-400' : ''}`}
                  onClick={() => setCompareMode('overlay')}
                >
                  Overlay
                </Button>
              </div>
            </div>
            
            {/* Overlay Opacity Slider (only in overlay mode) */}
            {compareMode === 'overlay' && (
              <div className="flex items-center gap-4 px-3">
                <SlidersHorizontal className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <Label className={`text-xs ${darkMode ? 'text-gray-400' : ''}`}>Newer Version Opacity:</Label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={overlayOpacity}
                  onChange={(e) => setOverlayOpacity(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
                <span className={`text-xs w-10 ${darkMode ? 'text-gray-400' : ''}`}>{overlayOpacity}%</span>
              </div>
            )}
            
            {/* Comparison View */}
            <div className={`rounded-lg overflow-hidden border ${darkMode ? 'border-[#2A2A2A] bg-[#0D0D0D]' : 'border-gray-200'}`} style={{ height: '500px' }}>
              {compareMode === 'side-by-side' ? (
                <div className="flex h-full">
                  {/* Left - Older Version */}
                  <div className={`flex-1 flex flex-col ${darkMode ? 'border-r border-[#2A2A2A]' : 'border-r border-gray-200'}`}>
                    <div className={`p-2 text-center text-xs font-medium ${darkMode ? 'bg-[#1A1A1A] text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                      v{compareVersions.v1?.version_number} (Older)
                      {compareVersions.v1?.revision_notes && (
                        <span className={`ml-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>- {compareVersions.v1.revision_notes}</span>
                      )}
                    </div>
                    <div className="flex-1 overflow-auto p-2 flex items-center justify-center">
                      {compareVersions.v1?.file_url ? (
                        <img 
                          src={compareVersions.v1.file_url.startsWith('http') ? compareVersions.v1.file_url : `${BACKEND_URL}${compareVersions.v1.file_url}`} 
                          alt={`Version ${compareVersions.v1.version_number}`}
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>No file uploaded for this version</p>
                      )}
                    </div>
                  </div>
                  {/* Right - Newer Version */}
                  <div className="flex-1 flex flex-col">
                    <div className={`p-2 text-center text-xs font-medium ${darkMode ? 'bg-[#1A1A1A] text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                      v{compareVersions.v2?.version_number} (Newer)
                      {compareVersions.v2?.revision_notes && (
                        <span className={`ml-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>- {compareVersions.v2.revision_notes}</span>
                      )}
                    </div>
                    <div className="flex-1 overflow-auto p-2 flex items-center justify-center">
                      {compareVersions.v2?.file_url ? (
                        <img 
                          src={compareVersions.v2.file_url.startsWith('http') ? compareVersions.v2.file_url : `${BACKEND_URL}${compareVersions.v2.file_url}`} 
                          alt={`Version ${compareVersions.v2.version_number}`}
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>No file uploaded for this version</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                /* Overlay Mode */
                <div className="h-full relative flex items-center justify-center overflow-auto p-4">
                  {/* Base Layer - Older Version */}
                  {compareVersions.v1?.file_url && (
                    <img 
                      src={compareVersions.v1.file_url.startsWith('http') ? compareVersions.v1.file_url : `${BACKEND_URL}${compareVersions.v1.file_url}`} 
                      alt={`Version ${compareVersions.v1.version_number}`}
                      className="max-w-full max-h-full object-contain absolute"
                    />
                  )}
                  {/* Overlay Layer - Newer Version */}
                  {compareVersions.v2?.file_url && (
                    <img 
                      src={compareVersions.v2.file_url.startsWith('http') ? compareVersions.v2.file_url : `${BACKEND_URL}${compareVersions.v2.file_url}`} 
                      alt={`Version ${compareVersions.v2.version_number}`}
                      className="max-w-full max-h-full object-contain absolute"
                      style={{ opacity: overlayOpacity / 100 }}
                    />
                  )}
                  {!compareVersions.v1?.file_url && !compareVersions.v2?.file_url && (
                    <p className="text-gray-500 text-sm">No files uploaded for selected versions</p>
                  )}
                </div>
              )}
            </div>
            
            {/* Version Details */}
            <div className={`grid grid-cols-2 gap-4 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'}`}>
                <p className="font-medium mb-1">v{compareVersions.v1?.version_number} Details</p>
                <p>Uploaded: {compareVersions.v1?.created_at ? new Date(compareVersions.v1.created_at).toLocaleDateString() : 'N/A'}</p>
                <p>By: {compareVersions.v1?.uploaded_by || 'Unknown'}</p>
                {compareVersions.v1?.preserved_markups?.length > 0 && (
                  <p className="text-yellow-500">{compareVersions.v1.preserved_markups.length} preserved markups</p>
                )}
              </div>
              <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'}`}>
                <p className="font-medium mb-1">v{compareVersions.v2?.version_number} Details</p>
                <p>Uploaded: {compareVersions.v2?.created_at ? new Date(compareVersions.v2.created_at).toLocaleDateString() : 'N/A'}</p>
                <p>By: {compareVersions.v2?.uploaded_by || 'Unknown'}</p>
                {compareVersions.v2?.markups?.length > 0 && (
                  <p className="text-green-500">{compareVersions.v2.markups.length} new markups</p>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompareDialog(false)} className={darkMode ? 'border-[#2A2A2A] text-gray-300' : ''}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog — Build 14.4 (2026-07-15): typed
          confirm ("DELETE"), stamps who/when via drawings_deletion_audit,
          register-only (Dropbox file untouched). */}
      <Dialog open={showDeleteConfirm} onOpenChange={(open) => {
        setShowDeleteConfirm(open);
        if (!open) setDeleteConfirmText('');
      }}>
        <DialogContent className={`max-w-md ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}`} data-testid="delete-drawing-dialog">
          <DialogHeader>
            <DialogTitle className={`text-red-600`}>
              Delete Drawing
            </DialogTitle>
            <DialogDescription className={darkMode ? 'text-[#8A8A8A]' : ''}>
              Remove <strong>{drawingToDelete?.drawing_number ? `${drawingToDelete.drawing_number} — ` : ''}{drawingToDelete?.title}</strong> from the register. The stored file on Dropbox is <strong>not</strong> deleted. This action stamps your name and the current time to an audit row. It cannot be undone from the UI.
              {drawingToDelete?.versions?.length > 1 && (
                <span className="block mt-2 text-amber-600">
                  This drawing has {drawingToDelete.versions.length} versions that will also be removed from the register.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-2">
            <label className={`text-xs uppercase tracking-wide ${darkMode ? 'text-[#8A8A8A]' : 'text-stone-500'}`}>
              Type DELETE to confirm
            </label>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
              data-testid="delete-drawing-confirm-input"
              className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-white' : ''}
            />
          </div>
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
              className={darkMode ? 'border-[#3A3A3A]' : ''}
              data-testid="delete-drawing-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteDrawing}
              disabled={deleteConfirmText !== 'DELETE'}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-40"
              data-testid="delete-drawing-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
